import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Heart, Search, Hand, MousePointerClick, X, Sparkles, MapPin, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import WallCanvas from "@/components/WallCanvas";
import DedicationModal from "@/components/DedicationModal";

const HERO_WALL =
  "https://static.prod-images.emergentagent.com/jobs/d75eefd7-e138-4cbb-9f59-667886564f01/images/aae3fa6ef69e51045178fe25540e7a6d006d71af73306ff167c5c958abcd1cdc.jpeg";

function HeartParticles() {
  const hearts = useMemo(
    () => Array.from({ length: 14 }).map((_, i) => ({
      id: i, left: Math.random() * 100, delay: Math.random() * 12, dur: 9 + Math.random() * 8,
      size: 10 + Math.random() * 18, op: 0.06 + Math.random() * 0.12,
    })), []);
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-0">
      {hearts.map((h) => (
        <Heart key={h.id} className="absolute text-rose-500 fill-rose-500"
          style={{ left: `${h.left}%`, bottom: "-40px", width: h.size, height: h.size,
            opacity: h.op, animation: `float-up ${h.dur}s linear ${h.delay}s infinite` }} />
      ))}
    </div>
  );
}

export default function LoveWall() {
  const [config, setConfig] = useState({ grid_size: 200, total_pixels: 40000, unit_price_eur: 1, max_selection: 500 });
  const [sold, setSold] = useState([]);
  const [stats, setStats] = useState({ sold: 0, total: 40000, available: 40000 });
  const [selected, setSelected] = useState(() => new Set());
  const [tool, setTool] = useState("select");
  const [hover, setHover] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const canvasRef = useRef(null);

  const soldSet = useMemo(() => new Set(sold.map((p) => p.index)), [sold]);
  const soldMap = useMemo(() => {
    const m = new Map();
    sold.forEach((p) => m.set(p.index, p));
    return m;
  }, [sold]);

  const loadData = useCallback(async () => {
    try {
      const [c, p, s] = await Promise.all([
        api.get("/config"), api.get("/pixels"), api.get("/stats"),
      ]);
      setConfig(c.data);
      setSold(p.data);
      setStats(s.data);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const togglePixel = useCallback((idx) => {
    if (soldSet.has(idx)) {
      const d = soldMap.get(idx);
      toast(`${d.dedicated_to}`, { description: d.message || `Dedicato da ${d.buyer_name}`, icon: "❤️" });
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }, [soldSet, soldMap]);

  const boxSelect = useCallback((list) => {
    setSelected((prev) => {
      const next = new Set(prev);
      let hitLimit = false;
      for (const idx of list) {
        if (soldSet.has(idx)) continue;
        if (next.size >= config.max_selection) { hitLimit = true; break; }
        next.add(idx);
      }
      if (hitLimit) toast.warning(`Massimo ${config.max_selection} pixel per acquisto`);
      return next;
    });
  }, [soldSet, config.max_selection]);

  const clearSelection = () => setSelected(new Set());

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!searchQ.trim()) { setSearchResults(null); return; }
    try {
      const { data } = await api.get("/dedications/search", { params: { q: searchQ.trim() } });
      setSearchResults(data);
      if (data.length === 0) toast.info("Nessuna dedica trovata");
    } catch { toast.error("Errore nella ricerca"); }
  };

  const jumpTo = (idx) => {
    canvasRef.current?.zoomToPixel(idx);
    document.getElementById("wall-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const count = selected.size;
  const soldPct = ((stats.sold / stats.total) * 100).toFixed(1);
  const recent = useMemo(() => [...sold].slice(-18).reverse(), [sold]);

  return (
    <div className="min-h-screen bg-[#0a0e17] text-slate-100 relative">
      <HeartParticles />

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/80 border-b border-rose-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <Heart className="text-rose-500 fill-rose-500 animate-heart-pulse" size={26} />
            </div>
            <div className="leading-none">
              <span className="font-display text-xl sm:text-2xl font-extrabold tracking-tight">Love Wall</span>
              <p className="text-[10px] uppercase tracking-[0.25em] text-rose-300/70">Le Mur des Je t'aime</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div data-testid="live-counter" className="hidden sm:flex flex-col items-end leading-none">
              <span className="text-rose-400 font-bold text-sm tabular-nums">{stats.sold.toLocaleString("it-IT")}</span>
              <span className="text-[10px] text-slate-500 uppercase tracking-wider">pixel dedicati</span>
            </div>
            <Button data-testid="header-buy-button" onClick={() => document.getElementById("wall-section")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-rose-600 hover:bg-rose-500 rounded-full px-5 h-10 font-semibold">
              Compra Pixel
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_WALL} alt="Muro dei Ti Amo Parigi" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/85 to-[#0a0e17]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rose-800/50 bg-rose-950/30 text-rose-200 text-xs uppercase tracking-widest mb-6">
            <MapPin size={13} /> Montmartre · Parigi
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50">
            Scrivi il tuo amore <br /> sul <span className="text-rose-500">Muro dei Ti Amo</span>
          </h1>
          <p className="mt-6 text-lg text-rose-100/80 font-light max-w-2xl mx-auto">
            Acquista uno o più pixel del muro leggendario e dedicali per sempre alla persona che ami.
            Ogni pixel costa solo <span className="text-amber-400 font-semibold">1&nbsp;€</span>.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button onClick={() => document.getElementById("wall-section")?.scrollIntoView({ behavior: "smooth" })}
              className="bg-rose-600 hover:bg-rose-500 rounded-full h-12 px-8 text-base font-semibold">
              <Heart className="mr-2 fill-white" size={18} /> Scegli i tuoi pixel
            </Button>
            <div className="text-sm text-slate-400">
              <span className="text-rose-300 font-semibold">{stats.available.toLocaleString("it-IT")}</span> pixel ancora liberi
            </div>
          </div>
          <div className="mt-8 max-w-md mx-auto">
            <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-rose-600 to-amber-400 transition-all duration-700" style={{ width: `${soldPct}%` }} />
            </div>
            <p className="text-xs text-slate-500 mt-2">{soldPct}% del muro dedicato</p>
          </div>
        </div>
      </section>

      {/* Wall */}
      <section id="wall-section" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-5">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-rose-50">Il Muro Interattivo</h2>
            <p className="text-slate-400 text-sm mt-1">
              Clicca un pixel per selezionarlo, trascina per selezionarne molti. Passa sopra i cuori per leggere le dediche.
            </p>
          </div>
          <form onSubmit={runSearch} className="flex gap-2 w-full lg:w-auto">
            <div className="relative flex-1 lg:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
              <Input data-testid="pixel-search-input" value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Cerca una dedica per nome..."
                className="pl-9 bg-slate-900 border-slate-700 h-11" />
            </div>
            <Button type="submit" data-testid="search-submit-button" variant="secondary" className="h-11 bg-slate-800 hover:bg-slate-700">Cerca</Button>
          </form>
        </div>

        {searchResults && (
          <div className="mb-5 p-4 rounded-xl bg-slate-900/60 border border-slate-800">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-slate-300">{searchResults.length} risultati per "{searchQ}"</p>
              <button onClick={() => { setSearchResults(null); setSearchQ(""); }} className="text-slate-500 hover:text-rose-400"><X size={16} /></button>
            </div>
            <div className="flex flex-wrap gap-2">
              {searchResults.slice(0, 40).map((r) => (
                <button key={r.index} onClick={() => jumpTo(r.index)}
                  className="px-3 py-1.5 rounded-full bg-rose-950/40 border border-rose-900/40 text-rose-200 text-xs hover:bg-rose-600 hover:text-white transition-colors">
                  {r.dedicated_to} · {r.buyer_name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="inline-flex rounded-lg bg-slate-900 border border-slate-800 p-1">
            <button data-testid="tool-select-button" onClick={() => setTool("select")}
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${tool === "select" ? "bg-rose-600 text-white" : "text-slate-400 hover:text-white"}`}>
              <MousePointerClick size={15} /> Seleziona
            </button>
            <button data-testid="tool-pan-button" onClick={() => setTool("pan")}
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${tool === "pan" ? "bg-rose-600 text-white" : "text-slate-400 hover:text-white"}`}>
              <Hand size={15} /> Sposta
            </button>
          </div>
          {count > 0 && (
            <button data-testid="clear-selection-button" onClick={clearSelection}
              className="px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 bg-slate-900 border border-slate-800 text-slate-400 hover:text-rose-400">
              <Trash2 size={14} /> Deseleziona ({count})
            </button>
          )}
        </div>

        <div className="relative rounded-2xl border-2 border-rose-950/50 bg-slate-950 p-1.5 shadow-2xl shadow-rose-950/20">
          <div className="relative h-[62vh] min-h-[420px] w-full rounded-lg overflow-hidden">
            <WallCanvas
              ref={canvasRef}
              gridSize={config.grid_size}
              tool={tool}
              soldSet={soldSet}
              soldMap={soldMap}
              selected={selected}
              onToggle={togglePixel}
              onBoxSelect={boxSelect}
              onHover={setHover}
            />
            {hover && hover.data && (
              <div className="pointer-events-none absolute z-30 max-w-[240px]"
                style={{ left: Math.min(hover.x + 14, 9999), top: hover.y + 14 }}>
                <div className="rounded-xl bg-slate-950/95 border border-rose-800/60 backdrop-blur p-3 shadow-xl">
                  {hover.data.photo_path && (
                    <img src={fileUrl(hover.data.photo_path)} alt="" className="w-full h-20 object-cover rounded-md mb-2" />
                  )}
                  <p className="font-hand text-2xl text-rose-200 leading-none">Per {hover.data.dedicated_to}</p>
                  {hover.data.message && <p className="text-sm text-slate-300 mt-1.5 leading-snug">{hover.data.message}</p>}
                  <p className="text-xs text-amber-400/90 mt-1.5">— {hover.data.buyer_name}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* selection bar */}
        <div className="sticky bottom-4 mt-4 z-30">
          <div className="mx-auto max-w-2xl rounded-2xl bg-slate-900/90 backdrop-blur-xl border border-rose-900/50 px-5 py-3 flex items-center justify-between gap-4 shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-400/15 grid place-items-center">
                <Sparkles className="text-amber-400" size={18} />
              </div>
              <div className="leading-tight">
                <p className="text-sm text-slate-400">Pixel selezionati</p>
                <p className="text-lg font-bold tabular-nums" data-testid="selected-count">
                  {count} <span className="text-amber-400 text-base font-semibold">= €{(count * config.unit_price_eur).toFixed(2)}</span>
                </p>
              </div>
            </div>
            <Button data-testid="buy-pixels-button" disabled={count === 0} onClick={() => setModalOpen(true)}
              className="bg-rose-600 hover:bg-rose-500 disabled:opacity-40 rounded-full h-11 px-6 font-semibold">
              <Heart className="mr-1.5 fill-white" size={16} /> Dedica ora
            </Button>
          </div>
        </div>
      </section>

      {/* Recent dedications */}
      {recent.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 relative z-10">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-rose-50 mb-6 text-center">Dediche recenti</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {recent.map((d) => (
              <button key={d.index} onClick={() => jumpTo(d.index)}
                className="group text-left rounded-xl bg-slate-900/60 border border-slate-800 p-4 hover:border-rose-700 transition-colors">
                {d.photo_path ? (
                  <img src={fileUrl(d.photo_path)} alt="" className="w-full h-20 object-cover rounded-md mb-2" />
                ) : (
                  <div className="w-full h-20 rounded-md mb-2 bg-[#16223f] grid place-items-center">
                    <Heart className="text-rose-600 fill-rose-600" size={22} />
                  </div>
                )}
                <p className="font-hand text-xl text-rose-200 leading-none truncate">Per {d.dedicated_to}</p>
                <p className="text-xs text-slate-400 mt-1 line-clamp-2 h-8">{d.message || "Con tutto il mio amore"}</p>
                <p className="text-[11px] text-amber-400/80 mt-1 truncate">— {d.buyer_name}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-rose-950/40 bg-slate-950 mt-8 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Heart className="text-rose-500 fill-rose-500" size={20} />
            <span className="font-display text-xl font-bold">Love Wall</span>
          </div>
          <p className="text-sm text-slate-500 max-w-lg mx-auto">
            Ispirato a "Le Mur des Je t'aime" di Montmartre, dove "ti amo" è scritto in oltre 250 lingue.
            Rendi eterno il tuo amore, un pixel alla volta.
          </p>
          <p className="text-xs text-slate-600 mt-4">© {new Date().getFullYear()} Love Wall · Fatto con ❤️ a Parigi</p>
        </div>
      </footer>

      <DedicationModal
        open={modalOpen}
        onOpenChange={(v) => { setModalOpen(v); }}
        count={count}
        unitPrice={config.unit_price_eur}
        pixels={Array.from(selected)}
      />
    </div>
  );
}
